/**
 * YachtDateRangeSelector Component
 * 
 * Yacht kiralama için tarih aralığı seçimi (Check-in / Check-out)
 * Travellers component'ine benzer dropdown yapısı
 */

'use client'

import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import DatePickerCustom from '@/components/ui/DatePickerCustom'
import { addDays, differenceInDays, format } from 'date-fns'
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext'

export interface YachtDateRangeSelectorProps {
  title?: string
  icon?: React.ReactNode
  checkInDate?: Date | null
  checkOutDate?: Date | null
  onCheckInChange?: (date: Date | null) => void
  onCheckOutChange?: (date: Date | null) => void
}

export default function YachtDateRangeSelector({
  title,
  icon,
  checkInDate,
  checkOutDate,
  onCheckInChange,
  onCheckOutChange,
}: YachtDateRangeSelectorProps) {
  const { getVariable } = useLanguageVariables()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Dışarıya tıklandığında menüyü kapat
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Özet metni - sadece gün sayısını göster
  const summaryText = useMemo(() => {
    if (!checkInDate && !checkOutDate) {
      return getVariable('yacht_date_range_select', 'Select dates')
    }
    
    if (checkInDate && checkOutDate) {
      const days = differenceInDays(checkOutDate, checkInDate)
      return `(${days} ${getVariable('days', 'days')})`
    }
    
    // Sadece check-in veya check-out seçildiyse placeholder göster
    return getVariable('yacht_date_range_select', 'Select dates')
  }, [checkInDate, checkOutDate, getVariable])

  // Check-in date değiştiğinde
  const handleCheckInSelect = useCallback((date: Date | undefined) => {
    onCheckInChange?.(date || null)
    // Eğer check-out date check-in'den önce veya eşitse, check-out'u temizle
    if (checkOutDate && date && checkOutDate <= date) {
      onCheckOutChange?.(null)
    }
  }, [checkOutDate, onCheckInChange, onCheckOutChange])

  // Check-out date değiştiğinde
  const handleCheckOutSelect = useCallback((date: Date | undefined) => {
    onCheckOutChange?.(date || null)
  }, [onCheckOutChange])

  return (
    <div ref={dropdownRef} className="w-full relative">
      {title && (
        <label className="text-alsero-dark-gray text-lg mb-1 block">
          {title}
        </label>
      )}
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="relative cursor-pointer flex flex-row justify-between items-center px-6 py-3 bg-white border border-gray-300 rounded-lg w-full min-h-[60px] hover:border-alsero-first transition-colors"
        >
          <div className="flex flex-row gap-2 items-center flex-1">
            {icon && <div className="flex-shrink-0">{icon}</div>}
            <span className="text-alsero-dark-gray text-lg text-left flex-1">
              {summaryText}
            </span>
          </div>
          <svg
            width="16"
            height="8"
            viewBox="0 0 16 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={`transition-transform duration-200 fill-alsero-dark-gray ${isOpen ? 'rotate-180' : ''}`}
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M14.7674 0.454837C15.1264 0.7968 15.1264 1.35123 14.7674 1.6932L8.63965 7.53088C8.2807 7.87284 7.69872 7.87284 7.33976 7.53088L1.21201 1.6932C0.853056 1.35123 0.853056 0.7968 1.21201 0.454836C1.57097 0.112873 2.15295 0.112873 2.5119 0.454836C5.57922 3.37696 10.4002 3.37696 13.4675 0.454837C13.8265 0.112873 14.4084 0.112873 14.7674 0.454837Z"
            />
          </svg>
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="absolute top-full left-0 mt-2 w-full bg-white rounded-xl shadow-2xl p-6 z-50 border border-gray-200"
            >
              <div className="flex flex-col gap-4">
                <div className="w-full">
                  <DatePickerCustom
                    label={getVariable('yacht_check_in', 'Check-in Date')}
                    selected={checkInDate || undefined}
                    onSelect={handleCheckInSelect}
                    disabled={{ before: new Date() }}
                  />
                </div>
                <div className="w-full">
                  <DatePickerCustom
                    label={getVariable('yacht_check_out', 'Check-out Date')}
                    selected={checkOutDate || undefined}
                    onSelect={handleCheckOutSelect}
                    disabled={{
                      before: checkInDate ? addDays(checkInDate, 1) : new Date(),
                    }}
                    isDisabled={!checkInDate}
                  />
                </div>
                {checkInDate && checkOutDate && (
                  <div className="pt-2 border-t border-gray-200">
                    <div className="text-sm text-alsero-dark-gray">
                      <span className="font-medium">
                        {differenceInDays(checkOutDate, checkInDate)}{' '}
                        {getVariable('days', 'days')}
                      </span>
                      {' '}
                      {getVariable('yacht_rental_period', 'rental period')}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

